import * as SDK from '@replai-platform/sdk';
import { camelCaseToCapitalCase, Tab, Tabs } from '@replai-platform/ui-components';
import { AnimatePresence } from 'framer-motion';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes, useLocation, useMatch, useNavigate } from 'react-router-dom';
import useHistoryState from 'use-history-state';
import { logEvent } from '../../../../analytics';
import useConceptPreviewInfo from '../../../../api/hooks/concepts/useConceptPreviewInfo';
import useConceptTags from '../../../../api/hooks/concepts/useConceptTags';
import { RootState } from '../../../../store/rootReducer';
import { formatDuration } from '../../../../utils';
import { Page } from '../../../../utils/enums';
import type { ConceptData } from '../../../../utils/types';
import { RouteAnimator } from '../../../RouteAnimator';
import { PROPERTY_TAG_TYPES } from '../../utils';
import ConceptMetrics from './ConceptMetrics';
import { MainContainer } from './shared/styles';
import * as Styled from './styles';
import TabPaths from './TabPaths';

const getTabLabel = (tab: string): string => {
  const cleanTab = tab.replace('-', ' ');
  return cleanTab[0].toUpperCase() + cleanTab.slice(1).toLowerCase();
};

interface ConceptViewTabsProps {
  conceptData: ConceptData;
  loading: boolean;
}

const ConceptViewTabs: React.VFC<ConceptViewTabsProps> = ({ conceptData, loading }) => {
  const dispatch = useDispatch();
  const match = useMatch(`:projectId/${Page.Concepts}/:clusterId/*`);
  const pathnameBase = match?.pathnameBase;
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useHistoryState(TabPaths.Metrics, 'currentTab');

  const tabs = useMemo(() => [TabPaths.Metrics], []);

  const location = useLocation();

  const projectId = useSelector((state: RootState) => state.project.id);

  const conceptId = conceptData?.id;
  const assetId = conceptData?.assetId;

  // Tag data is also used to get the right preview of the concept.
  const { data: tagData, isLoading } = useConceptTags({ projectId, assetId }, conceptId, { enabled: !!conceptId });
  const { data: conceptPreviewData } = useConceptPreviewInfo({
    conceptId,
    size: '200x200',
    ...(assetId ? { assetId } : {}),
    ...(projectId ? { projectId } : {}),
  });

  const tabLabels: Tab[] = useMemo<Tab[]>(
    () =>
      tabs.map((tab) => ({
        label: getTabLabel(tab),
        trailingBadge: undefined,
        disabled: false,
        tooltipContent: undefined,
      })),
    [tabs]
  );

  useEffect(() => {
    if (location.pathname.indexOf(TabPaths.Metrics) > 0 && currentTab !== TabPaths.Metrics)
      setCurrentTab(TabPaths.Metrics, true);
    else if (!loading && !tabs.some((tab) => location.pathname.indexOf(tab) > 0)) {
      setCurrentTab(TabPaths.Metrics, true);
      navigate(`${pathnameBase ?? location.pathname}/${TabPaths.Metrics}${location.search}`, {
        replace: true,
      });
    }
  }, [
    currentTab,
    setCurrentTab,
    location,
    tabs,
    pathnameBase,
    loading,
    navigate,
    dispatch,
    conceptData?.name,
    conceptData?.displayName,
  ]);

  const onChangeTab = (tab: Tab, tabIndex: number) => {
    navigate(`${pathnameBase ?? location.pathname}/${tabs[tabIndex]}${location.search}`, {
      replace: true,
    });
    logEvent({
      component: 'Concept Details',
      action: `click ${camelCaseToCapitalCase(tabs[tabIndex])}`,
      category: 'user_actions',
    });
    setCurrentTab(tabs[tabIndex], true);
  };

  const propertyData = useMemo(() => {
    const durationProperty = {
      key: 'Duration',
      value: tagData?.duration ? formatDuration(tagData?.duration) : '00:00:00',
    };
    const propertiesTags =
      tagData?.tags
        .filter((tag) => PROPERTY_TAG_TYPES.includes(tag.type as SDK.CoreTags) && !!tag.value)
        .map((tag) => ({
          key: camelCaseToCapitalCase(tag.type),
          value: camelCaseToCapitalCase(tag.value || '-'),
        })) ?? [];

    return [...propertiesTags, durationProperty].sort((a, b) => a.key.localeCompare(b.key));
  }, [tagData?.duration, tagData?.tags]);

  return (
    <MainContainer>
      <Tabs
        variant="gray-button"
        tabLabels={tabLabels}
        onTabChange={onChangeTab}
        defaultSelectedTab={tabLabels.find((tab) => tab.label === getTabLabel(currentTab))}
      />
      <Styled.Container>
        <Styled.TabContainer>
          <AnimatePresence exitBeforeEnter>
            <Routes>
              <Route
                path={`${TabPaths.Metrics}/*`}
                element={
                  <RouteAnimator>
                    <ConceptMetrics
                      conceptData={conceptData}
                      propertyData={propertyData}
                      url={tagData?.url ?? (isLoading ? '' : conceptPreviewData?.previewUrl) ?? ''}
                      isLoading={isLoading}
                    />
                  </RouteAnimator>
                }
              />
            </Routes>
          </AnimatePresence>
        </Styled.TabContainer>
      </Styled.Container>
    </MainContainer>
  );
};

export default ConceptViewTabs;
